import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TaskCard = ({ task, onComplete, isCompleted }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleComplete = () => {
    if (isCompleted) return;
    
    setIsAnimating(true);
    setTimeout(() => {
      onComplete(task?.id, task?.xp);
      setIsAnimating(false);
    }, 600);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 border-error/20';
      case 'medium':
        return 'bg-warning/10 border-warning/20';
      case 'low':
        return 'bg-success/10 border-success/20';
      default:
        return 'bg-muted/10 border-muted/20';
    }
  };

  return (
    <div
      className={`bg-card border rounded-lg transition-all duration-300 ${
        isCompleted
          ? 'border-success/30 bg-success/5' :'border-border hover:border-primary/30 hover:shadow-elevation-1'
      } ${isAnimating ? 'animate-scale-in' : ''}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          <button
            onClick={handleComplete}
            disabled={isCompleted}
            className={`shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200 ${
              isCompleted
                ? 'bg-success border-success cursor-default' :'border-muted-foreground hover:border-primary hover:bg-primary/10 cursor-pointer'
            }`}
            aria-label={isCompleted ? 'Task completed' : 'Mark task as complete'}
          >
            {isCompleted && (
              <Icon name="Check" size={16} color="var(--color-success-foreground)" strokeWidth={3} />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3
                className={`font-semibold text-foreground ${
                  isCompleted ? 'line-through opacity-60' : ''
                }`}
              >
                {task?.title}
              </h3>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityBg(
                    task?.priority
                  )} ${getPriorityColor(task?.priority)}`}
                >
                  {task?.priority?.toUpperCase()}
                </span>
                <div className="flex items-center gap-1 bg-primary/10 px-2 py-1 rounded">
                  <Icon name="Zap" size={14} color="var(--color-primary)" strokeWidth={2.5} />
                  <span className="text-sm font-bold text-primary">+{task?.xp}</span>
                </div>
              </div>
            </div>

            <p className={`text-sm text-muted-foreground mb-3 ${isCompleted ? 'opacity-60' : ''}`}>
              {task?.description}
            </p>

            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Clock" size={14} color="currentColor" />
                <span>{task?.estimatedTime}</span>
              </div>
              {task?.category && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Tag" size={14} color="currentColor" />
                  <span>{task?.category}</span>
                </div>
              )}
            </div>

            {task?.resources && task?.resources?.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                iconPosition="right"
                className="mb-2"
              >
                {isExpanded ? 'Hide' : 'Show'} Resources
              </Button>
            )}

            {isExpanded && task?.resources && (
              <div className="mt-3 p-3 bg-muted/20 rounded-lg space-y-2 animate-fade-in">
                {task?.resources?.map((resource, index) => (
                  <a
                    key={index}
                    href={resource?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-accent hover:text-accent/80 transition-colors duration-150"
                  >
                    <Icon name="ExternalLink" size={14} color="currentColor" />
                    <span>{resource?.title}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;